import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CaretLeft } from "@phosphor-icons/react";
import { solutionByKey, SOLUTIONS } from "@/data/solutions";

export default function SolutionPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const sol = solutionByKey(key);

  if (!sol) {
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="text-navy font-heading text-2xl">Solution not found.</p>
        <Link to="/" className="text-gold font-semibold mt-4 inline-block">Back home</Link>
      </div>
    );
  }

  const Icon = sol.icon;
  const others = SOLUTIONS.filter((s) => s.key !== key);

  return (
    <div data-testid={`solution-page-${sol.key}`}>
      {/* hero */}
      <section className="bg-navy grain relative pt-[120px] pb-20 lg:pt-[150px] lg:pb-28">
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8">
          <button onClick={() => navigate(-1)} data-testid="solution-back"
            className="inline-flex items-center gap-1.5 text-light-slate hover:text-warm-white text-sm mb-10 transition-colors">
            <CaretLeft size={16} /> Back
          </button>
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="grid place-items-center w-14 h-14 rounded-xl bg-gold/15 text-gold mb-6">
                <Icon weight="duotone" size={30} />
              </span>
              <p className="eyebrow text-gold mb-3">{sol.audience}</p>
              <h1 className="font-heading font-extrabold tracking-tighter text-warm-white text-[clamp(2.4rem,6vw,4.4rem)] leading-[0.98]">
                {sol.title}
              </h1>
              <p className="mt-6 text-light-slate text-lg leading-relaxed max-w-xl">{sol.intro}</p>
              <Link to="/" state={{ scrollTo: "booking" }} data-testid="solution-cta"
                className="mt-8 group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-navy font-semibold hover:brightness-110 transition-all">
                Book a free consultation
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="clip-frame overflow-hidden rounded-xl border border-white/10">
                <img src={sol.image} alt={sol.audience} className="w-full h-[340px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* products */}
      <section className="bg-warm-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-2 gap-4 mb-16">
            {sol.points.map((p, i) => {
              const PIcon = p.icon;
              return (
                <motion.div key={p.name}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-2xl border border-black/[0.08] bg-white p-8">
                  <span className="grid place-items-center w-12 h-12 rounded-lg bg-navy/[0.06] text-navy mb-5">
                    <PIcon weight="duotone" size={26} />
                  </span>
                  <h3 className="font-heading font-bold text-navy text-xl">{p.name}</h3>
                  <p className="mt-3 text-brand-slate leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="max-w-3xl space-y-12">
            {sol.sections.map((s) => (
              <motion.div key={s.h}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5 }}>
                <h2 className="font-heading font-extrabold text-navy text-[clamp(1.6rem,3vw,2.4rem)] tracking-tight leading-tight">
                  {s.h}
                </h2>
                <p className="mt-4 text-brand-slate text-lg leading-relaxed">{s.p}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* other solutions */}
      <section className="bg-slate-navy grain relative py-20">
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8">
          <p className="eyebrow text-gold mb-8">Explore other solutions</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {others.map((s) => {
              const OIcon = s.icon;
              return (
                <Link key={s.key} to={`/solutions/${s.key}`} data-testid={`other-solution-${s.key}`}
                  className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] hover:border-gold/40 transition-all">
                  <span className="grid place-items-center w-12 h-12 shrink-0 rounded-lg bg-gold/15 text-gold">
                    <OIcon weight="duotone" size={24} />
                  </span>
                  <div>
                    <p className="text-light-slate text-xs">{s.audience}</p>
                    <p className="font-heading font-bold text-warm-white text-lg group-hover:text-gold transition-colors">{s.title}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
